// CORE
import { DateTime } from 'luxon';
import startCase from 'lodash/startCase';
import React from 'react';
import { useSelector } from 'react-redux';

// DATA
import { accountsSelectors } from 'app-components/flux/accounts';
import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import { getSmartDateLabel } from 'app-components/utils/date';
import { numberUtils } from 'app-components/utils/number';

import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// CUSTOM COMPONENTS
import ConnectedAvatar from 'components/ConnectedAvatar';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import { OverflowTooltip } from 'components/OverflowTooltip';
import { TxnActionsMenuButton } from 'components/TxnActionHelpers/TxnActionsMenu';

const useStyles = makeStyles<{ past?: true; isIncome: boolean; positive: boolean }>()(
  (theme, { past, isIncome, positive }) => ({
    root: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: past ? '16px' : '16px',
      boxShadow: past ? '' : '0px 0px 4px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.1)',
      backgroundColor: past ? '' : theme.palette.background.paper,
      border: past ? '' : `1px solid ${theme.palette.border.light}`,
      borderBottom: past && `1px solid ${theme.palette.border.light}`,
      height: 78,
      overflow: 'hidden',
      borderRadius: past ? '' : 8,
      '&:hover': {
        borderRadius: 8,
        boxShadow: past ? '' : '0px 0px 2px rgba(51, 51, 51, 0.48), 0px 4px 10px rgba(51, 51, 51, 0.16)',
        background: past ? 'theme.palette.action.hover' : theme.palette.background.paper,
        borderBottom: past ? `1px solid ${theme.palette.border.light}` : `0px solid ${theme.palette.border.light}`,
      },
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flexGrow: 2,
      overflow: 'hidden',
      minWidth: 0,
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-end',
      flexGrow: 0,
      flexShrink: 0,
      marginRight: theme.spacing(1),
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 8,
      fontWeight: 500,
      flexGrow: 0,
      display: past ? 'none' : '',
      marginRight: 16,
      color: theme.palette.common.white,
      backgroundColor: past
        ? (isIncome && theme.palette.legendDim.income) || theme.palette.legendDim.bill
        : (isIncome && theme.palette.legend.income) || theme.palette.legend.bill,
    },
    amount: {
      color: `${positive ? theme.palette.amount.positive : theme.palette.text.primary} !important`,
      width: '100%',
    },
    status: {
      borderRadius: 12,
      padding: '0 12px',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.warning.light,
      height: 24,
      lineHeight: '24px',
    },
    comingSoonRow: {
      display: 'grid',
      gridTemplateColumns: '48px minmax(0, 1.6fr) minmax(0, 1fr) auto auto',
      columnGap: theme.spacing(2),
    },
    truncationWrapper: {
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      display: 'block',
    },
    actionsColumn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
  }),
);

interface ViewRowProps {
  past?: true;
  txn: TransactionRecord;
  isIncome: boolean;
  baseId?: string;
  model?: ScheduledTransactionType;
  rootModalDrawer?: RootModalDrawerOptions;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                   Row Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ViewRow({ past, txn, isIncome, baseId, model, rootModalDrawer }: ViewRowProps) {
  const { payee, amount, id, accountId } = txn;

  const positive = Number(amount) > 0;
  const { classes } = useStyles({ isIncome, positive, past });

  const accountsById = useSelector(accountsSelectors.getAccountsById);
  const account = accountsById && accountsById.get(accountId || '');

  const today = DateTime.local().startOf('day');
  const txnDate = transactionsUtils.dateTimeFromTxn(txn);
  const dayDiff = txnDate.diff(today, 'days').days;
  const dayString = getSmartDateLabel(txn.postedOn || txn.stDueOn || '');
  const accountTypeDisplay = account?.subType
    ? startCase(account.subType.toLowerCase())
    : account?.type
      ? startCase(account.type.toLowerCase())
      : '';

  const formatAmount = useFormatAmount();

  const renderComingSoonRow = () => {
    return (
      <div id={`${baseId}-row`} className={`${classes.root} ${classes.comingSoonRow}`} key={id}>
        <ConnectedAvatar
          seriesIcon
          isCurrentCategoryIncome={isIncome}
          model={model}
          txn={txn}
          size={44}
          iconSize={20}
        />
        <div className={classes.leftColumn}>
          <OverflowTooltip title={payee || ''}>
            <Typography
              id={`${baseId}-payee`}
              variant='bodyRegular'
              noWrap
              color='text.primary'
              className={classes.truncationWrapper}
            >
              {payee}
            </Typography>
          </OverflowTooltip>
          <Typography id={`${baseId}-days`} variant='paragraphRegular' color='text.secondary'>
            {dayString}
          </Typography>
        </div>
        <div className={classes.leftColumn}>
          <Typography id={`${baseId}-account-name`} variant='paragraphRegular' color='text.primary' noWrap>
            {account ? account.name : 'Somewhere'}
          </Typography>
          <Typography id={`${baseId}-type`} variant='paragraphRegular' color='text.secondary' noWrap>
            {accountTypeDisplay}
          </Typography>
        </div>
        <div className={classes.rightColumn}>
          <Typography id={`${baseId}-amount`} variant='bodyMedium' noWrap className={classes.amount}>
            {formatAmount(amount, 'USD', '0,0.00', numberUtils.ShowSignEnum.POSITIVE_ONLY)}
          </Typography>
          {txn.source === 'SCHEDULED_TRANSACTION_PENDING' && dayDiff < 0 && (
            <Typography variant='captionRegular' className={classes.status}>
              Past
            </Typography>
          )}
        </div>
        {!past && (
          <div className={classes.actionsColumn}>
            <TxnActionsMenuButton txn={txn} context={'view-row'} rootModalDrawer={rootModalDrawer} />
          </div>
        )}
      </div>
    );
  };

  const renderLinkedTransactionRows = () => {
    return (
      <div id={`${baseId}-row`} className={classes.root} key={id}>
        <Avatar className={classes.avatar}>{payee ? payee[0].toUpperCase() : '!'}</Avatar>
        <div className={classes.leftColumn}>
          <Typography id={`${baseId}-amount`} variant='bodyMedium' noWrap className={classes.amount}>
            {formatAmount(amount, 'USD', '0,0.00', numberUtils.ShowSignEnum.POSITIVE_ONLY)}
          </Typography>
          <OverflowTooltip title={account ? account.name : 'Somewhere'}>
            <Typography id={`${baseId}-account-name`} variant='paragraphRegular' color='text.secondary' noWrap>
              {account ? account.name : 'Somewhere'}
            </Typography>
          </OverflowTooltip>
        </div>
        <div className={classes.rightColumn}>
          <Typography id={`${baseId}-month-day`} variant='paragraphRegular'>
            {dayString}
          </Typography>

          {txn.source === 'SCHEDULED_TRANSACTION_PENDING' && dayDiff < 0 && (
            <Typography variant='captionRegular' className={classes.status}>
              Past
            </Typography>
          )}
        </div>
        {!past && <TxnActionsMenuButton txn={txn} context={'view-row'} rootModalDrawer={rootModalDrawer} />}
      </div>
    );
  };

  return baseId === 'coming-soon' ? renderComingSoonRow() : renderLinkedTransactionRows();
}

export default ViewRow;
